/** Снимки и навигация по вариантам ответа AI. */

import {
    CHARTABLE_TYPES,
    CHART_TYPE_OPTIONS,
    normalizeChartViewConfig,
} from '@/components/chat/cards/chartViewConfig'

export const REGEN_CHART_FORMAT_OPTIONS = [
    {
        id: 'line',
        label: 'Линейный',
        aiHint: 'displayFormat=line — линейный график по периодам (серии как линии)',
    },
    {
        id: 'stacked-bar',
        label: 'Накопительный',
        aiHint: 'displayFormat=stacked-bar — накопительные столбцы по периодам',
    },
    {
        id: 'bar',
        label: 'Столбцы',
        aiHint: 'displayFormat=bar — групповые столбцы по периодам',
    },
    {
        id: 'pie',
        label: 'Пирог',
        aiHint: 'displayFormat=pie — круговая: один SQL GROUP BY brand (без year/_dataset), seriesCols=[brand], topN≤8, доли брендов',
    },
]

const FORMAT_ID_BY_LABEL = Object.fromEntries(
    REGEN_CHART_FORMAT_OPTIONS.map(o => [o.label, o.id]),
)

export function chartTypeFromFormatLabel(formatLabel) {
    if (!formatLabel) return null
    return FORMAT_ID_BY_LABEL[formatLabel] || null
}

/** Индексы карточек-графиков в порядке появления. */
export function chartableCardIndexes(cards = []) {
    return cards
        .map((card, index) => ({ card, index }))
        .filter(({ card }) => CHARTABLE_TYPES.has(card.cardType))
        .map(({ index }) => index)
}

/** Применить выбранный формат отображения к карточкам графика. */
export function applyChartFormatsToMessage(msg, chartFormats = []) {
    if (!msg?.cards?.length || !chartFormats?.length) return
    const chartableIdx = chartableCardIndexes(msg.cards)
    for (const fmt of chartFormats) {
        const idx = Number.isFinite(fmt.chartableIndex)
            ? chartableIdx[fmt.chartableIndex]
            : fmt.cardIndex
        const card = typeof idx === 'number' ? msg.cards[idx] : null
        if (!card || !CHARTABLE_TYPES.has(card.cardType)) continue
        card.viewConfig = normalizeChartViewConfig({
            chartType: fmt.chartType || 'line',
            hiddenSeries: [],
        })
    }
}

export function applyChartFormatFromLabel(msg, formatLabel) {
    const chartType = chartTypeFromFormatLabel(formatLabel)
    if (!chartType) return
    applyChartFormatsToMessage(
        msg,
        (msg.cards || [])
            .map((card, cardIndex) => ({ cardIndex, chartType }))
            .filter(({ cardIndex }) => CHARTABLE_TYPES.has(msg.cards[cardIndex]?.cardType)),
    )
}

/** format_label «Линейный, Пирог» — по одному типу на каждый график по порядку. */
export function applyChartFormatsFromFormatLabel(msg, formatLabel) {
    if (!formatLabel || !msg?.cards?.length) return
    const labels = String(formatLabel)
        .split(',')
        .map(s => s.trim())
        .filter(Boolean)
    if (!labels.length) return

    const chartableIdx = chartableCardIndexes(msg.cards)
    if (labels.length === 1) {
        const chartType = chartTypeFromFormatLabel(labels[0])
        if (chartType) applyChartFormatFromLabel(msg, labels[0])
        return
    }

    labels.forEach((label, i) => {
        const chartType = chartTypeFromFormatLabel(label)
        if (!chartType || i >= chartableIdx.length) return
        const cardIndex = chartableIdx[i]
        msg.cards[cardIndex].viewConfig = normalizeChartViewConfig({
            chartType,
            hiddenSeries: [],
        })
    })
}

/** Парсинг служебной инструкции перегенерации: «Заголовок»: displayFormat=pie */
export function parseChartFormatsFromRegenText(text) {
    if (!text || typeof text !== 'string') return []
    const formats = []
    const re = /«([^»]+)»:\s*displayFormat=(\w+)/g
    let m
    while ((m = re.exec(text)) !== null) {
        const chartType = m[2]
        if (CHART_TYPE_OPTIONS.some(o => o.id === chartType)) {
            formats.push({ title: m[1].trim(), chartType })
        }
    }
    return formats
}

function cardTitleMatches(cardTitle, hintTitle) {
    const a = String(cardTitle || '').trim().toLowerCase()
    const b = String(hintTitle || '').trim().toLowerCase()
    if (!a || !b) return false
    return a === b || a.includes(b) || b.includes(a)
}

/** Применить форматы из текста перегенерации к карточкам по заголовку. */
export function applyChartFormatsByTitle(msg, formats = []) {
    if (!msg?.cards?.length || !formats?.length) return
    for (const fmt of formats) {
        const idx = msg.cards.findIndex(
            c => CHARTABLE_TYPES.has(c.cardType)
                && cardTitleMatches(c.payload?.title, fmt.title),
        )
        if (idx < 0) continue
        msg.cards[idx].viewConfig = normalizeChartViewConfig({
            chartType: fmt.chartType,
            hiddenSeries: [],
        })
    }
}

function chartFormatsFromVariant(v) {
    const raw = v?.chart_formats ?? v?.chartFormats
    if (!Array.isArray(raw)) return []
    return raw
        .map((f, ordinal) => ({
            cardIndex: Number.isFinite(f.card_index) ? f.card_index : f.cardIndex,
            chartableIndex: Number.isFinite(f.chartable_index)
                ? f.chartable_index
                : (Number.isFinite(f.chartableIndex) ? f.chartableIndex : ordinal),
            chartType: f.chart_type || f.chartType || 'line',
            title: f.title || '',
        }))
        .filter(f => f.chartType)
}

const FORMAT_LABEL_BY_ID = Object.fromEntries(
    REGEN_CHART_FORMAT_OPTIONS.map(o => [o.id, o.label]),
)

function deepClone(value) {
    if (value == null) return value
    return JSON.parse(JSON.stringify(value))
}

export function snapshotAiMessage(msg, meta = {}) {
    return {
        savedAt: new Date().toISOString(),
        formatLabel: meta.formatLabel || null,
        text: msg.text || '',
        textContent: deepClone(msg.textContent),
        textMode: msg.textMode || 'plain',
        source: msg.source ? { ...msg.source } : null,
        sources: deepClone(msg.sources || []),
        chartFormats: (msg.cards || [])
            .map((card, cardIndex) => {
                if (!CHARTABLE_TYPES.has(card.cardType) || !card.viewConfig?.chartType) return null
                return {
                    cardIndex,
                    chartableIndex: chartableCardIndexes(msg.cards).indexOf(cardIndex),
                    chartType: card.viewConfig.chartType,
                    title: card.payload?.title || '',
                }
            })
            .filter(Boolean),
        cards: (msg.cards || []).map(c => ({
            cardType: c.cardType,
            payload: deepClone(c.payload),
            viewConfig: c.viewConfig
                ? normalizeChartViewConfig(c.viewConfig)
                : undefined,
        })),
        lists: deepClone(msg.lists || []),
        callouts: deepClone(msg.callouts || []),
        followups: deepClone(msg.followups),
        usage: msg.usage ? { ...msg.usage } : null,
    }
}

export function applySnapshotToMessage(msg, snap) {
    if (!msg || !snap) return
    msg.text = snap.text
    msg.textContent = snap.textContent
    msg.textMode = snap.textMode
    msg.source = snap.source
    msg.sources = snap.sources || []
    msg.cards = (snap.cards || []).map(c => ({ ...c }))
    msg.lists = snap.lists || []
    msg.callouts = snap.callouts || []
    msg.followups = snap.followups
    msg.usage = snap.usage
}

export function ensureInitialVersion(msg) {
    if (!msg || msg.role !== 'ai') return
    if (!Array.isArray(msg.versions)) {
        msg.versions = []
    }
    if (msg.versions.length === 0 && msg.status === 'done') {
        msg.versions.push(snapshotAiMessage(msg))
        msg.activeVersionIndex = 0
    }
}

export function commitNewVersion(msg, formatLabel = null) {
    if (!msg || msg.role !== 'ai' || msg.status !== 'done') return
    if (!Array.isArray(msg.versions)) {
        msg.versions = []
    }
    if (msg.versions.length === 0) {
        msg.versions.push(snapshotAiMessage(msg, { formatLabel }))
        msg.activeVersionIndex = 0
        return
    }
    const label = formatLabel || null
    const snap = snapshotAiMessage(msg, { formatLabel: label })
    const prev = msg.versions[msg.versions.length - 1]
    if (prev && prev.text === snap.text && JSON.stringify(prev.cards) === JSON.stringify(snap.cards)) {
        return
    }
    msg.versions.push(snap)
    msg.activeVersionIndex = msg.versions.length - 1
}

export function versionCount(msg) {
    return Array.isArray(msg?.versions) ? msg.versions.length : 0
}

export function formatLabelForRegen(chartFormats) {
    if (!chartFormats?.length) return 'Перегенерация'
    const parts = chartFormats
        .map(f => FORMAT_LABEL_BY_ID[f.chartType] || f.chartType)
        .filter(Boolean)
    return parts.length ? parts.join(', ') : 'Перегенерация'
}

export function buildRegenerationPrompt(userText, chartFormats = [], extraInstructions = '') {
    const lines = (chartFormats || [])
        .map(f => {
            const opt = REGEN_CHART_FORMAT_OPTIONS.find(o => o.id === f.chartType)
            const title = f.title || 'График'
            return opt
                ? `- «${title}»: ${opt.aiHint}`
                : null
        })
        .filter(Boolean)

    const formatBlock = lines.length
        ? `ОБЯЗАТЕЛЬНЫЙ формат графиков (make_chart_from_dataset, поле displayFormat):\n${lines.join('\n')}`
        : 'Перестрой визуализацию ответа (графики, списки, выводы).'

    const extra = (extraInstructions || '').trim()
    const extraBlock = extra
        ? `\nДополнительные пожелания пользователя:\n${extra}`
        : ''

    return `${userText.trim()}

---
Служебная инструкция (перегенерация ответа): пересобери ответ на запрос выше. Сохрани смысл анализа и те же фильтры; период — только из текста запроса пользователя и активных чипов, не из прошлых заголовков карточек. ${formatBlock}${extraBlock}
Для КАЖДОГО графика заново: query_clickhouse_excel (если нужны свежие данные) → make_chart_from_dataset с указанным displayFormat.
Нельзя оставлять line/stacked-bar/bar, если пользователь выбрал pie (и наоборот). displayFormat в tool ОБЯЗАН совпадать с выбором пользователя.
Для pie: один SQL GROUP BY brand (или region), seriesCols только [brand] без year/_dataset, topN≤8, доли по брендам в легенде.
Используй compare-chart / market-chart, списки и callout там, где уместно.`
}

export const RESPONSE_VARIANTS_CHUNK_TYPE = 'response_variants'

/** @param {object} v — вариант с бэкенда (snake_case). */
export function mapServerVariant(v) {
    if (!v || typeof v !== 'object') return null
    return {
        savedAt: v.saved_at || v.savedAt || null,
        formatLabel: v.format_label ?? v.formatLabel ?? null,
        text: v.text || '',
        textContent: v.text_content ?? v.textContent ?? null,
        textMode: v.text_mode || v.textMode || 'plain',
        source: v.source || null,
        sources: Array.isArray(v.sources) ? [...v.sources] : [],
        chartFormats: chartFormatsFromVariant(v),
        cards: (v.cards || []).map(c => ({
            cardType: c.cardType || c.card_type,
            payload: deepClone(c.payload),
            viewConfig: c.viewConfig || c.view_config
                ? normalizeChartViewConfig(c.viewConfig || c.view_config)
                : undefined,
        })),
        lists: deepClone(v.lists || []),
        callouts: deepClone(v.callouts || []),
        followups: v.followups ? deepClone(v.followups) : null,
        usage: v.usage ? { ...v.usage } : null,
    }
}

export function parseResponseVariants(serverMsg) {
    const fromTop = serverMsg?.response_variants || serverMsg?.responseVariants
    if (fromTop?.variants?.length) {
        return {
            variants: fromTop.variants.map(mapServerVariant).filter(Boolean),
            active_index: fromTop.active_index ?? fromTop.activeIndex ?? 0,
        }
    }
    const chunks = serverMsg?.chunks
    if (!Array.isArray(chunks)) return null
    const chunk = chunks.find(c => c?.type === RESPONSE_VARIANTS_CHUNK_TYPE)
    if (!chunk?.variants?.length) return null
    return {
        variants: chunk.variants.map(mapServerVariant).filter(Boolean),
        active_index: chunk.active_index ?? chunk.activeIndex ?? 0,
    }
}

export function applyVersionsToMessage(msg, parsed) {
    if (!msg || !parsed?.variants?.length) return
    msg.versions = parsed.variants
    const idx = Number(parsed.active_index)
    msg.activeVersionIndex = Number.isFinite(idx) && idx >= 0 && idx < msg.versions.length
        ? idx
        : msg.versions.length - 1
    const snap = msg.versions[msg.activeVersionIndex]
    applySnapshotToMessage(msg, snap)
    const hasViewConfig = (snap?.cards || []).some(c => c?.viewConfig?.chartType)
    if (!hasViewConfig && snap?.chartFormats?.length) {
        applyChartFormatsToMessage(msg, snap.chartFormats)
    } else if (!hasViewConfig && snap?.formatLabel) {
        applyChartFormatsFromFormatLabel(msg, snap.formatLabel)
    }
}

/**
 * Для админки: взять последние служебные user-сообщения после AI-ответа
 * и восстановить displayFormat по заголовкам карточек.
 */
export function inferChartFormatsFromFollowingUserMessages(messages, aiMessageIndex) {
    const inferred = []
    for (let i = aiMessageIndex + 1; i < messages.length; i += 1) {
        const m = messages[i]
        if (m?.role === 'assistant' || m?.role === 'ai') break
        if (m?.role !== 'user') continue
        for (const fmt of parseChartFormatsFromRegenText(m.text || '')) {
            const exists = inferred.some(f => cardTitleMatches(f.title, fmt.title))
            if (!exists) inferred.push(fmt)
        }
    }
    return inferred
}

export function syncVersionsFromMeta(msg, responseVariants, pendingChartFormats = null) {
    if (!msg || !responseVariants?.variants?.length) return
    applyVersionsToMessage(msg, {
        variants: responseVariants.variants.map(mapServerVariant).filter(Boolean),
        active_index: responseVariants.active_index ?? responseVariants.activeIndex ?? 0,
    })
    if (pendingChartFormats?.length) {
        applyChartFormatsToMessage(msg, pendingChartFormats)
    }
}

export function resetAiMessageForStream(msg) {
    msg.status = 'streaming'
    msg.text = ''
    msg.textContent = null
    msg.textMode = 'plain'
    msg.cards = []
    msg.lists = []
    msg.callouts = []
    msg.followups = null
    msg.sources = []
    msg.source = null
    msg.usage = null
    msg.progress = ''
    msg.progressSteps = []
    msg.requestId = undefined
}
